<script setup>
import { computed } from 'vue';
import { request } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check, Edit } from 'lucide-vue-next';

const props = defineProps({
  editState: Object,
});

const emit = defineEmits(['update']);

const editForm = computed(() => {
  return props.editState.form;
});

function closeEditModal() {
  props.editState.open = false;
}

async function saveDesign() {
  if (!props.editState || !props.editState.item) return;

  try {
    // Send update to server
    await request(`/library/${props.editState.item.id}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(editForm.value),
    });

    emit('update');
    closeEditModal();
  } catch (error) {
    console.error('Failed to update design:', error);
  }
}
</script>
<template>
  <Dialog v-model:open="editState.open">
    <DialogContent
      class="sm:max-w-xl bg-white dark:bg-[#151515] p-0 rounded-xl border border-black/5 dark:border-black/80 shadow-xl"
    >
      <DialogHeader class="px-6 pt-6 pb-4 border-b border-black/5 dark:border-black/80">
        <DialogTitle
          class="text-lg font-semibold text-black/90 dark:text-white flex items-center gap-2"
        >
          <Edit class="h-4 w-4 text-black/50 dark:text-black/40" />
          {{ $t('library.edit') }}
        </DialogTitle>
      </DialogHeader>

      <div class="p-6 py-0 grid grid-cols-1 gap-6">
        <!-- Left Column - Basic Info -->
        <div class="space-y-5">
          <div>
            <label class="block text-sm font-medium text-black/70 dark:text-black/20 mb-2">{{
              $t('library.name')
            }}</label>
            <input
              v-model="editForm.name"
              type="text"
              class="w-full px-3 py-2 bg-white dark:bg-[#1E1E1E] border border-black/10 dark:border-black/70 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 text-black/80 dark:text-black/10 text-sm transition-all duration-200"
              :placeholder="$t('library.name')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-black/70 dark:text-black/20 mb-2">{{
              $t('library.editDescription')
            }}</label>
            <textarea
              v-model="editForm.description"
              rows="3"
              class="w-full px-3 py-2 bg-white dark:bg-[#1E1E1E] border border-black/10 dark:border-black/70 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 text-black/80 dark:text-black/10 text-sm transition-all duration-200"
              :placeholder="$t('library.noDescription')"
            ></textarea>
          </div>
        </div>
      </div>

      <DialogFooter
        class="flex justify-between px-6 py-4 border-t border-black/5 dark:border-black/80 bg-black/5 dark:bg-[#1A1A1A] rounded-b-xl"
      >
        <div class="flex gap-2">
          <button
            @click="closeEditModal"
            class="px-3 py-1.5 bg-white dark:bg-[#1E1E1E] border border-black/10 dark:border-black/70 text-black/70 dark:text-black/20 text-sm rounded-md hover:bg-black/5 dark:hover:bg-black/80 transition-all duration-200"
          >
            {{ $t('library.cancel') }}
          </button>
          <button
            @click="saveDesign"
            class="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-all duration-200 flex items-center gap-1.5"
          >
            <Check class="h-3.5 w-3.5" />
            {{ $t('library.save') }}
          </button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
